/// <reference path="../vite-env.d.ts" />
import { useEffect, useState } from 'react';
import { useKeycloak } from "@react-keycloak/web";
import { useProjectsStore } from "../state/projects";
import { getProjectItems } from "../api/projects";
import { getProjectActionItems } from "../api/actionItems";
import { LIST_MAX_SIZE } from "../types/pagination";
import { toast } from 'react-toastify';
import { UnifiedTask, convertProjectItemToUnifiedTask, convertActionItemToUnifiedTask } from '../types/task';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router';
import { BsThreeDotsVertical, BsArrowDown, BsArrowUp } from 'react-icons/bs';
import { CreateDropdown } from '../components/CreateDropdown';
import { NewProjectForm } from '../components/NewProjectForm';
import { NotificationButton } from '../components/NotificationButton';

type SortField = 'dueDate' | 'status' | 'priority';
type SortDirection = 'asc' | 'desc';

/** Backend sort field for each sortable column. */
const SORT_FIELD_TO_API: Record<SortField, string> = {
  dueDate: 'due_date',
  status: 'status',
  priority: 'priority',
};

const Tasks = () => {
  const { keycloak: { token } } = useKeycloak();
  const navigate = useNavigate();
  const projects = useProjectsStore((state) => state.projects);
  const [tasks, setTasks] = useState<UnifiedTask[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [showNewProject, setShowNewProject] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isTasksLoading, setIsTasksLoading] = useState<boolean>(false);
  const [tasksTruncated, setTasksTruncated] = useState<boolean>(false);

  // Tasks come from the per-project endpoints, filtered on the backend. The project dropdown
  // chooses which projects are queried; each project returns at most 200 items per kind.
  useEffect(() => {
    if (!projects || !token) return;

    const accessToken: string = token;
    const targets = (selectedProject === 'all' ? projects : projects.filter((project) => project.id === selectedProject))
      .filter((project): project is ProjectInterface & { id: string } => !!project.id);
    const query = {
      size: LIST_MAX_SIZE,
      statusValues: selectedStatus !== 'all' ? [selectedStatus] : undefined,
      priorityValues: selectedPriority !== 'all' ? [selectedPriority] : undefined,
      sort: [{ field: SORT_FIELD_TO_API[sortField], direction: sortDirection }],
    };
    let cancelled = false;
    setIsTasksLoading(true);

    const loadProject = async (project: ProjectInterface & { id: string }) => {
      const [itemsPage, actionItemsPage] = await Promise.all([
        getProjectItems(accessToken, project.id, query),
        getProjectActionItems(accessToken, project.id, query),
      ]);
      const truncated = itemsPage.total > itemsPage.results.length || actionItemsPage.total > actionItemsPage.results.length;
      return {
        tasks: [
          ...itemsPage.results.map((item) => convertProjectItemToUnifiedTask(item, project)),
          ...actionItemsPage.results.map((item) => convertActionItemToUnifiedTask(item, project)),
        ],
        truncated,
      };
    };

    // Bounded concurrency so a large portfolio does not open hundreds of requests at once.
    const results: { tasks: UnifiedTask[]; truncated: boolean }[] = new Array(targets.length);
    let next = 0;
    const worker = async () => {
      while (next < targets.length) {
        const index = next++;
        results[index] = await loadProject(targets[index]);
      }
    };

    Promise.all(Array.from({ length: Math.min(5, targets.length) }, worker))
      .then(() => {
        if (cancelled) return;
        setTasks(results.flatMap((result) => result.tasks));
        setTasksTruncated(results.some((result) => result.truncated));
      })
      .catch((error: unknown) => {
        console.error('Failed to load tasks:', error);
        toast.error('Failed to load tasks');
      })
      .finally(() => {
        if (!cancelled) setIsTasksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projects, token, selectedProject, selectedStatus, selectedPriority, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filtered and sorted by the backend; the list is shown in the order each project returns it.
  const sortedAndFilteredTasks = tasks;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-primary/10 text-primary';
      case 'NOT_STARTED':
        return 'bg-[#f5f5f5] text-[#171717]';
      default:
        return 'bg-[#f5f5f5] text-[#171717]';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-orange-100 text-orange-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-[#f5f5f5] text-[#171717]';
    }
  };

  const getSourceLabel = (source: 'project_plan' | 'action_item') => {
    return source === 'project_plan' ? 'Project Plan' : 'Action Item';
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <BsArrowUp className="inline ml-1" /> : <BsArrowDown className="inline ml-1" />;
  };

  return (
    <div className="flex flex-col w-full h-full p-6">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tasks</h1>
        <div className="flex items-center gap-4">
          <CreateDropdown onSelectProject={() => setShowNewProject(true)} />
          <NotificationButton />
          <select
            className="bg-white border border-[#eaeaea] rounded-lg px-4 py-2 text-sm"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white border border-[#eaeaea] rounded-lg px-4 py-2 text-sm"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            className="bg-white border border-[#eaeaea] rounded-lg px-4 py-2 text-sm"
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
        </div>
      </div>
      {tasksTruncated && (
        <div className="text-[12px] text-[#888] px-1 pb-2">
          Some projects have more than 200 matching tasks or action items; only the first 200 of each are shown.
        </div>
      )}
      {isTasksLoading && (
        <div className="text-[12px] text-[#888] px-1 pb-2">Loading tasks…</div>
      )}
      <div className="flex-1 bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#eaeaea]">
            <thead className="bg-[#fafafa]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Task
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Project
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Phase
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider cursor-pointer hover:text-[#444]"
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date <SortIcon field="dueDate" />
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider cursor-pointer hover:text-[#444]"
                  onClick={() => handleSort('status')}
                >
                  Status <SortIcon field="status" />
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider cursor-pointer hover:text-[#444]"
                  onClick={() => handleSort('priority')}
                >
                  Priority <SortIcon field="priority" />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Source
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-[#888] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#eaeaea]">
              {sortedAndFilteredTasks.map((task) => (
                <tr key={`${task.source}-${task.id}`} className="hover:bg-[#fafafa]">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-[#171717]">{task.name}</div>
                    {task.description && (
                      <div className="text-sm text-[#888] mt-1">{task.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link 
                      to={`/projects/${task.projectId}`}
                      className="text-sm text-[#171717] hover:text-primary"
                    >
                      {task.projectName}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#888]">
                    {task.phaseName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#888]">
                    {task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(task.status)}`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#888]">
                    {getSourceLabel(task.source)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-[#aaa] hover:text-[#555]">
                      <BsThreeDotsVertical className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Tasks; 